/**
 * Keeps the music playing across pages (Framer behaviour). While <mrk-player> has a track loaded
 * (body.mrk--player-open), internal links don't reload the page: the next page is fetched, its new stylesheets are
 * loaded first, then only the header, main content and footer are swapped. The player, cart drawer and dialogs live
 * outside those regions, so the audio never stops. When nothing is playing, links behave normally.
 *
 * Anything unusual falls back to a normal page load: checkout, cart page, account, apps, downloads, other sites,
 * modified clicks, failed requests, or a response that isn't one of our theme pages.
 */
const REGIONS = ['#header-group', '#MainContent', '.page-wrapper > footer'];
const EXCLUDED_PATH = /^\/(cart|checkout|checkouts|account|apps|admin|password|challenge|tools|services|policies\/.*\.pdf)(\/|$)/;
const FILE_EXT = /\.(pdf|zip|mp3|wav|png|jpe?g|gif|webp|svg|json|xml|txt)$/i;

let navigationId = 0;

function isActive() {
  return document.body.classList.contains('mrk--player-open') && !window.Shopify?.designMode;
}

/** @param {URL} url */
function isEligibleUrl(url) {
  if (url.origin !== location.origin) return false;
  if (EXCLUDED_PATH.test(url.pathname) || FILE_EXT.test(url.pathname)) return false;
  if (url.pathname === location.pathname && url.search === location.search && url.hash) return false;
  return true;
}

/** @param {MouseEvent} event */
function onClick(event) {
  if (event.defaultPrevented || !isActive()) return;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;
  if (link.target && link.target !== '_self') return;
  if (link.hasAttribute('download') || link.dataset.mrkHardNav !== undefined) return;
  const url = new URL(link.href, location.href);
  if (!isEligibleUrl(url)) return;
  event.preventDefault();
  navigate(url, { push: true });
}

/** @param {Document} doc */
async function loadNewStylesheets(doc) {
  const have = new Set([...document.querySelectorAll('link[rel="stylesheet"]')].map((link) => link.href));
  const pending = [];
  for (const selector of REGIONS) {
    for (const link of doc.querySelectorAll(`${selector} link[rel="stylesheet"]`)) {
      const href = new URL(link.getAttribute('href') || '', location.href).href;
      if (have.has(href)) continue;
      have.add(href);
      const preload = document.createElement('link');
      preload.rel = 'stylesheet';
      preload.href = href;
      pending.push(
        new Promise((resolve) => {
          preload.onload = preload.onerror = resolve;
          setTimeout(resolve, 3000);
        })
      );
      document.head.append(preload);
    }
  }
  await Promise.all(pending);
}

/** Scripts inserted with markup don't run; recreate them so section scripts work like a normal load. */
function runScripts(root) {
  for (const old of root.querySelectorAll('script')) {
    const type = old.getAttribute('type');
    if (type && !['module', 'text/javascript', 'application/javascript'].includes(type)) continue;
    const script = document.createElement('script');
    for (const { name, value } of old.attributes) script.setAttribute(name, value);
    script.textContent = old.textContent;
    old.replaceWith(script);
  }
}

/** @param {Document} doc */
function updateHead(doc) {
  document.title = doc.title;
  for (const selector of ['meta[name="description"]', 'link[rel="canonical"]', 'meta[property="og:title"]', 'meta[property="og:url"]']) {
    const next = doc.head.querySelector(selector);
    const current = document.head.querySelector(selector);
    if (next && current) current.replaceWith(next.cloneNode(true));
  }
}

/** @param {string} hash */
function scrollAfterSwap(hash) {
  const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
  if (target) {
    target.scrollIntoView();
    return;
  }
  window.scrollTo(0, 0);
  document.querySelector('.page-wrapper')?.scrollTo?.(0, 0);
}

/**
 * @param {URL} url
 * @param {{ push: boolean }} options
 */
async function navigate(url, { push }) {
  const id = ++navigationId;
  document.documentElement.classList.add('mrk-navigating');
  try {
    const response = await fetch(url.href, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
    const finalUrl = new URL(response.url || url.href);
    const type = response.headers.get('content-type') || '';
    if (!response.ok || !type.includes('text/html') || !isEligibleUrl(finalUrl)) throw new Error('hard');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (!doc.body.classList.contains('mrk') || REGIONS.some((selector) => !doc.querySelector(selector))) {
      throw new Error('hard');
    }
    if (id !== navigationId) return;

    await loadNewStylesheets(doc);
    if (id !== navigationId) return;

    if (push) history.pushState({ mrk: true }, '', finalUrl.href + url.hash);
    updateHead(doc);
    for (const selector of REGIONS) {
      const current = document.querySelector(selector);
      const next = doc.querySelector(selector);
      if (!current || !next) continue;
      const adopted = document.adoptNode(next);
      current.replaceWith(adopted);
      runScripts(adopted);
    }
    scrollAfterSwap(url.hash);

    const main = document.getElementById('MainContent');
    if (main) {
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('mrk:page:load', { detail: { url: finalUrl.href } }));
  } catch {
    if (id === navigationId) location.assign(url.href);
  } finally {
    if (id === navigationId) document.documentElement.classList.remove('mrk-navigating');
  }
}

if (!window.mrkRouterBound && 'DOMParser' in window && history.pushState) {
  window.mrkRouterBound = true;
  history.replaceState({ ...(history.state || {}), mrk: true }, '');
  // Window listeners run after document listeners, so components that handle their own links
  // (license dialog, menus) have already called preventDefault when they need to.
  window.addEventListener('click', onClick);
  window.addEventListener('popstate', (event) => {
    if (!event.state?.mrk) return;
    if (!isActive()) {
      location.reload();
      return;
    }
    navigate(new URL(location.href), { push: false });
  });
}
